import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import './Analytics.css';

const Analytics = ({ orderData, orders = [], customers = [] }) => {
  // Pie chart data - Order Status Distribution
  const orderStatusData = [
    { name: 'Completed', value: orderData.delivered || 0, color: '#70F9C5' },
    { name: 'In Progress', value: orderData.inProgress || 0, color: '#667eea' },
    { name: 'Cancelled', value: orderData.cancelled || 0, color: '#FF6B6B' },
  ];

  // Calculate customer growth from real data
  const calculateCustomerGrowth = () => {
    if (customers.length === 0) {
      // Fallback to calculated from orders if no customer data
      const phoneOrderCounts = {};
      orders.forEach(o => {
        if (o.customer?.phone) {
          phoneOrderCounts[o.customer.phone] = (phoneOrderCounts[o.customer.phone] || 0) + 1;
        }
      });
      const newCustomers = Object.values(phoneOrderCounts).filter(c => c === 1).length;
      const returningCustomers = Object.values(phoneOrderCounts).filter(c => c > 1).length;
      return [
        { name: 'New', value: newCustomers || 1, color: '#4ECDC4' },
        { name: 'Returning', value: returningCustomers || 1, color: '#667eea' },
      ];
    }
    const newCount = customers.filter(c => c.tag === 'new' || c.totalOrders === 1).length;
    const returningCount = customers.filter(c => c.tag === 'regular' || c.tag === 'loyal' || c.totalOrders > 1).length;
    return [
      { name: 'New', value: newCount || 1, color: '#4ECDC4' },
      { name: 'Returning', value: returningCount || 1, color: '#667eea' },
    ];
  };
  const customerGrowthData = calculateCustomerGrowth();

  // Calculate revenue distribution from real orders
  const calculateRevenueDistribution = () => {
    const deliveryRevenue = orders
      .filter(o => o.orderType === 'delivery' && o.status !== 'cancelled')
      .reduce((sum, o) => sum + (o.pricing?.total || o.total || 0), 0);
    const takeawayRevenue = orders
      .filter(o => o.orderType === 'takeaway' && o.status !== 'cancelled')
      .reduce((sum, o) => sum + (o.pricing?.total || o.total || 0), 0);
    const total = deliveryRevenue + takeawayRevenue;
    return [
      { name: 'Delivery', value: total > 0 ? Math.round((deliveryRevenue / total) * 100) : 50, color: '#3B82F6' },
      { name: 'Takeaway', value: total > 0 ? Math.round((takeawayRevenue / total) * 100) : 50, color: '#F59E0B' },
    ];
  };
  const revenueData = calculateRevenueDistribution();

  // Calculate weekly order trend from real orders
  const calculateWeeklyOrders = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayCounts = days.map(day => ({ day, orders: 0 }));

    // Get last 7 days of orders
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    orders.forEach(order => {
      const orderDate = order.timestamp?.toDate ? order.timestamp.toDate() : new Date(order.timestamp);
      if (orderDate >= sevenDaysAgo) {
        const dayIndex = orderDate.getDay();
        dayCounts[dayIndex].orders++;
      }
    });

    return dayCounts;
  };
  const weeklyOrderData = calculateWeeklyOrders();

  // Calculate monthly revenue from real orders (current year vs last year)
  const calculateMonthlyRevenue = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentYear = new Date().getFullYear();
    const lastYear = currentYear - 1;

    const monthlyData = months.map((month, index) => ({
      month,
      [`revenue${lastYear}`]: 0,
      [`revenue${currentYear}`]: 0
    }));

    orders.forEach(order => {
      if (order.status === 'cancelled') return;
      const orderDate = order.timestamp?.toDate ? order.timestamp.toDate() : new Date(order.timestamp);
      const year = orderDate.getFullYear();
      const monthIndex = orderDate.getMonth();
      const total = order.pricing?.total || order.total || 0;

      if (year === currentYear) {
        monthlyData[monthIndex][`revenue${currentYear}`] += total;
      } else if (year === lastYear) {
        monthlyData[monthIndex][`revenue${lastYear}`] += total;
      }
    });

    return monthlyData;
  };
  const monthlyRevenueData = calculateMonthlyRevenue();

  // Calculate customer activity map from real orders
  const calculateCustomerMap = () => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const dayCustomers = days.map(day => ({ day, customers: 0 }));

    // Get last 7 days
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const customersByDay = {};

    orders.forEach(order => {
      const orderDate = order.timestamp?.toDate ? order.timestamp.toDate() : new Date(order.timestamp);
      if (orderDate >= sevenDaysAgo) {
        const dayIndex = orderDate.getDay();
        const adjustedIndex = dayIndex === 0 ? 6 : dayIndex - 1; // Adjust for Mon=0
        if (!customersByDay[adjustedIndex]) {
          customersByDay[adjustedIndex] = new Set();
        }
        if (order.customer?.phone) {
          customersByDay[adjustedIndex].add(order.customer.phone);
        }
      }
    });

    Object.keys(customersByDay).forEach(dayIndex => {
      dayCustomers[parseInt(dayIndex)].customers = customersByDay[dayIndex].size;
    });

    return dayCustomers;
  };
  const customerMapData = calculateCustomerMap();

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: '16px', fontWeight: 'bold' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="analytics-section">
      {/* Pie Charts Section */}
      <div className="analytics-row">
        <div className="chart-card chart-pie-section">
          <div className="chart-header">
            <h3 className="chart-title">Pie Chart</h3>
            <div className="chart-controls">
              <label className="chart-checkbox">
                <input type="checkbox" defaultChecked />
                <span>Chart</span>
              </label>
              <label className="chart-checkbox">
                <input type="checkbox" defaultChecked />
                <span>Show Value</span>
              </label>
            </div>
          </div>

          <div className="pie-charts-container">
            {/* Total Orders Pie */}
            <div className="pie-chart-item">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={orderStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={80}
                    innerRadius={45}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {orderStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <p className="pie-label">Total Order</p>
            </div>

            {/* Customer Growth Pie */}
            <div className="pie-chart-item">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={customerGrowthData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={80}
                    innerRadius={45}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {customerGrowthData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <p className="pie-label">Customer Growth</p>
            </div>

            {/* Revenue Distribution Pie */}
            <div className="pie-chart-item">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={revenueData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={80}
                    innerRadius={45}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {revenueData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <p className="pie-label">Total Revenue</p>
            </div>
          </div>
        </div>

        {/* Chart Order - Area Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <div>
              <h3 className="chart-title">Chart Order</h3>
              <p className="chart-subtitle">Lorem ipsum dolor sit amet, consectetur adipis</p>
            </div>
            <button className="save-report-btn">
              <span>📥</span> Save Report
            </button>
          </div>

          <div className="chart-container">
            <div className="chart-legend-info">
              <span className="legend-value">456</span>
              <span className="legend-label">Order</span>
              <span className="legend-date">04, Sept 2024</span>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={weeklyOrderData}>
                <defs>
                  <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8ecef" />
                <XAxis dataKey="day" stroke="#6f767e" fontSize={12} />
                <YAxis stroke="#6f767e" fontSize={12} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="orders"
                  stroke="#3B82F6"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorOrders)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Second Row: Revenue Line Chart and Customer Map Bar Chart */}
      <div className="analytics-row">
        {/* Total Revenue Line Chart */}
        <div className="chart-card chart-large">
          <div className="chart-header">
            <h3 className="chart-title">Total Revenue</h3>
            <div className="legend-row">
              <span className="legend-item">
                <span className="legend-dot legend-dot-blue"></span> 2024
              </span>
              <span className="legend-item">
                <span className="legend-dot legend-dot-red"></span> 2025
              </span>
            </div>
          </div>

          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyRevenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8ecef" />
                <XAxis dataKey="month" stroke="#6f767e" fontSize={12} />
                <YAxis stroke="#6f767e" fontSize={12} />
                <Tooltip
                  formatter={(value) => `₹${value.toLocaleString()}`}
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #e8ecef',
                    borderRadius: '8px',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="revenue2024"
                  stroke="#3B82F6"
                  strokeWidth={3}
                  dot={{ fill: '#3B82F6', r: 5 }}
                  activeDot={{ r: 7 }}
                />
                <Line
                  type="monotone"
                  dataKey="revenue2025"
                  stroke="#EF4444"
                  strokeWidth={3}
                  dot={{ fill: '#EF4444', r: 5 }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Customer Map Bar Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Customer Map</h3>
            <select className="chart-select">
              <option>Weekly</option>
              <option>Monthly</option>
              <option>Yearly</option>
            </select>
          </div>

          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={customerMapData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8ecef" vertical={false} />
                <XAxis dataKey="day" stroke="#6f767e" fontSize={12} />
                <YAxis stroke="#6f767e" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #e8ecef',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="customers" radius={[8, 8, 0, 0]}>
                  {customerMapData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={index % 2 === 0 ? '#FF6B6B' : '#F7B731'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
